import type { FocusedElement } from '../state';
import type { OptionCallPut } from '../types';
import { useStableArray } from './useStableArray';
import { useOptionsSymbolSearchService } from './useSymbolSearchService';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getSymbolSearchServiceQuery, selectDates, selectExactMatches } from 'src/queries/symbolSearchService';

export function useValidDates({
    underlyingSymbol,
    strikePrice,
    optionCallPut,
    onUpdate,
}: {
    underlyingSymbol?: string;
    strikePrice?: number;
    optionCallPut?: OptionCallPut;
    onUpdate: (element: FocusedElement, items: any[], loading?: boolean) => void;
}) {
    const { symbolSearchService } = useOptionsSymbolSearchService();

    const validDatesSelector = useMemo(
        () =>
            selectExactMatches({
                underlyingSymbol,
                strikePrice,
                optionCallPut,
                select: selectDates,
            }),
        [underlyingSymbol, strikePrice, optionCallPut],
    );

    const { data: validDates = [], isLoading: validDatesLoading } = useQuery({
        ...getSymbolSearchServiceQuery(underlyingSymbol ?? '', symbolSearchService),
        select: validDatesSelector,
    });

    // Stabilize validDates array to prevent unnecessary re-renders
    const stableValidDates = useStableArray(validDates, (date) => String(date));

    useEffect(() => {
        if (validDatesLoading) {
            onUpdate('expirationDate', [], true);
        } else if (stableValidDates.length > 0) {
            onUpdate('expirationDate', stableValidDates, false);
        }
    }, [stableValidDates, validDatesLoading, onUpdate]);
}
